import { executeApiCommand, type ApiCommandInput } from "./api.ts";

const configuredUrl = import.meta.env?.VITE_DIRECTUS_URL || "auto";
export const DIRECTUS_URL = configuredUrl === "auto"
  ? `${globalThis.location?.origin ?? "http://localhost:8055"}/api`
  : configuredUrl;

let isLoggingOut = false;

export function resetLoggingOutState(): void {
  isLoggingOut = false;
}

type StoredAuth = { access_token: string; expires?: number; refresh_token?: string | null } | null;
const storage = {
  get: async (): Promise<StoredAuth> => {
    try { return JSON.parse(localStorage.getItem("cordite_auth") || "null"); } catch { return null; }
  },
  set: async (data: StoredAuth) => {
    if (data) localStorage.setItem("cordite_auth", JSON.stringify(data));
    else localStorage.removeItem("cordite_auth");
  },
};

export function clearAuthStorage(): void {
  try {
    localStorage.removeItem("cordite_role");
    localStorage.removeItem("cordite_auth");
    localStorage.removeItem("cordite_masquerade_backup");
    localStorage.removeItem("cordite_masquerade_meta");
    localStorage.removeItem("cordite_prefs");
    localStorage.removeItem("cordite.favorites");
  } catch { /* ignore */ }
  try {
    sessionStorage.clear();
  } catch { /* ignore */ }
}

export function handleUnauthorized(_reason?: string): void {
  clearAuthStorage();

  try { directus.stopRefreshing(); } catch { /* ignore */ }
  try { void directus.logout().catch(() => {}); } catch { /* ignore */ }

  if (isLoggingOut) return;
  isLoggingOut = true;
  if (typeof window !== "undefined" && window.location) {
    const path = window.location.pathname || "";
    if (!path.startsWith("/login") && !path.startsWith("/logout") && !path.startsWith("/portal/invite")) {
      window.location.replace("/login");
    }
  }
}

export const authFetch: typeof fetch = async (input, init) => {
  const response = await fetch(input, init);
  if (response.status === 401) {
    const url = typeof input === "string" ? input : input instanceof Request ? input.url : String(input);
    if (!/\/auth\/login(\?|$)/.test(url)) handleUnauthorized(`401 from ${url}`);
  }
  return response;
};

if (typeof window !== "undefined" && typeof window.fetch === "function") {
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await nativeFetch(input, init);
    if (response.status === 401) {
      const url = typeof input === "string" ? input : input instanceof Request ? input.url : String(input);
      if (!/\/auth\/login(\?|$)/.test(url)) handleUnauthorized(`401 from ${url}`);
    }
    return response;
  };
}

export const directus = {
  url: DIRECTUS_URL,

  async getToken(): Promise<string | null> {
    return (await storage.get())?.access_token ?? null;
  },

  async request<T = unknown>(command: ApiCommandInput<T>): Promise<T> {
    return executeApiCommand({ baseUrl: DIRECTUS_URL, getToken: () => directus.getToken(), fetch: authFetch }, command);
  },

  async login(credentials: { email: string; password: string }, options?: { otp?: string }) {
    const result = await executeApiCommand<{ token: string; user: unknown }>({
      baseUrl: DIRECTUS_URL,
      getToken: () => null,
      fetch: authFetch,
    }, {
      method: "POST",
      path: "/auth/login",
      body: { ...credentials, otp: options?.otp },
    });
    await storage.set({ access_token: result.token, expires: 7 * 24 * 60 * 60 * 1000 });
    return result;
  },

  async logout(): Promise<void> {
    try {
      await executeApiCommand({ baseUrl: DIRECTUS_URL, getToken: () => directus.getToken(), fetch: authFetch }, {
        method: "POST", path: "/auth/logout",
      });
    } finally {
      await storage.set(null);
    }
  },

  stopRefreshing(): void { /* Bearer tokens do not need a refresh timer. */ },
};
